import { useState, type ReactNode } from "react";
import { GripVertical, Trash2 } from "lucide-react";

interface DragListProps<T> {
  items: T[];
  onChange: (items: T[]) => void;
  onRemove: (index: number) => void;
  renderItem: (item: T, index: number) => ReactNode;
  addLabel: string;
  onAdd: () => void;
}

// DragList gives every admin-authored ordered collection (gallery images,
// resource-pack files, audio slides, quiz options, form fields, ...) the
// same native HTML5 drag-and-drop reordering, keyboard-free but with a
// visible grip handle and delete button per row.
export function DragList<T>({ items, onChange, onRemove, renderItem, addLabel, onAdd }: DragListProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  function drop(targetIndex: number) {
    if (dragIndex === null || dragIndex === targetIndex) return;
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    onChange(next);
    setDragIndex(null);
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      {items.map((item, index) => (
        <div
          key={index}
          draggable
          onDragStart={() => setDragIndex(index)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => drop(index)}
          className="flex items-start gap-2 rounded-md border border-border bg-surface p-3"
        >
          <span className="mt-2 cursor-grab text-content-disabled" aria-hidden="true">
            <GripVertical size={16} />
          </span>
          <div className="min-w-0 flex-1">{renderItem(item, index)}</div>
          <button
            type="button"
            className="mt-1 shrink-0 text-content-secondary hover:text-danger"
            onClick={() => onRemove(index)}
            aria-label="Remove"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="w-fit rounded-md border border-dashed border-border-strong px-3 py-2 text-sm font-medium text-interactive hover:bg-interactive/5"
        onClick={onAdd}
      >
        + {addLabel}
      </button>
    </div>
  );
}
